import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bell, Download, Fingerprint, LogOut } from 'lucide-react';
import { Botao, Cartao } from '../../../web/src/componentes/base';
import { Entrada, Rotulo, Selecao } from '../../../web/src/componentes/formulario';
import { Modal } from '../../../web/src/componentes/sobreposicao';
import { useToast } from '../../../web/src/componentes/toast';
import { mensagemDeErro } from '../../../web/src/nucleo/api';
import { posicaoNaPagina, recuperarFoco } from '../../../web/src/nucleo/foco';
import type { Idioma } from '../../../web/src/nucleo/formato';
import { baixar, chamar, pget, ppatch, ppost } from '../api';
import { trocarIdioma } from '../i18n';
import { nomeDoAparelho, push, sairDeTodos } from '../push';
import { definirModoSimples, useModoSimples } from '../modo-simples';
import { cadastrarPasskey, removerPasskey, temPasskey } from '../passkey';
import { CHAVE_SESSAO, nomeEmpresa, useSessao } from '../sessao';
import { dataCurta } from './comum';

function aplicarTema(tema: 'sistema' | 'claro' | 'escuro') {
  try {
    if (tema === 'sistema') localStorage.removeItem('malgo.portal.tema');
    else localStorage.setItem('malgo.portal.tema', tema);
  } catch {
    /* sem storage */
  }
  const claro = window.matchMedia?.('(prefers-color-scheme: light)').matches;
  document.documentElement.dataset.tema = tema === 'sistema' ? (claro ? 'claro' : 'escuro') : tema;
}

// U15 F4: 150% entra junto dos 125% (a escala do portal fica só no aparelho, sem banco).
type EscalaPortal = '100' | '110' | '125' | '150';

function aplicarEscala(escala: EscalaPortal) {
  try {
    localStorage.setItem('malgo.portal.escala', escala);
  } catch {
    /* sem storage */
  }
  if (escala === '100') delete document.documentElement.dataset.escala;
  else document.documentElement.dataset.escala = escala;
}

const lido = (chave: string) => {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
};

function Avisos() {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const toast = useToast();
  const [ativo, setAtivo] = useState(false);
  const suporte = push.suporte();
  useEffect(() => {
    void push.ativo().then(setAtivo);
  }, []);
  if (!s.push.chave_publica) return null;
  return (
    <Cartao titulo={t('perfil.avisos')}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-2">{t('perfil.avisosTexto')}</p>
        {suporte === 'ios_instalar' && <p className="text-sm text-tinta-2">{t('perfil.avisosIos')}</p>}
        {suporte === 'negado' && <p className="text-sm text-perigo">{t('perfil.avisosNegados')}</p>}
        {suporte === 'nao' && <p className="text-sm text-tinta-3">{t('perfil.avisosSemSuporte')}</p>}
        {suporte === 'sim' &&
          (ativo ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-verde">{t('perfil.avisosAtivos')}</span>
              <Botao
                tamanho="sm"
                data-avisos-push=""
                onClick={async () => {
                  await push.desativar();
                  setAtivo(false);
                  // Review r29 visual (B2): o botão troca por "Ativar avisos"; o foco segue nele em vez de cair no body.
                  recuperarFoco({ reservas: ['[data-avisos-push]'] });
                }}
              >
                {t('perfil.desativarAvisos')}
              </Botao>
            </div>
          ) : (
            <Botao
              icone={<Bell className="size-4" aria-hidden />}
              disabled={s.somente_leitura}
              data-avisos-push=""
              onClick={async () => {
                const r = await push.ativar(s.push.chave_publica!).catch(() => 'erro' as const);
                if (r === 'ok') {
                  setAtivo(true);
                  recuperarFoco({ reservas: ['[data-avisos-push]'] });
                }
                else toast.erro(r === 'negado' ? t('perfil.avisosNegados') : t('perfil.avisosSemSuporte'));
              }}
            >
              {t('perfil.ativarAvisos')}
            </Botao>
          ))}
      </div>
    </Cartao>
  );
}

/**
 * Review r23 segurança (B3): a prova recente que a mudança de segurança exige (pôr, trocar ou tirar a senha; cadastrar
 * passkey): a senha atual ou o código novo do e-mail. Só vai o que foi digitado; o código vale sobre a senha.
 */
export function provaDaConfirmacao(atual: string, codigo: string): { atual?: string; codigo?: string } {
  const c = codigo.replace(/\D/g, '');
  if (c.length === 6) return { codigo: c };
  return atual ? { atual } : {};
}

/**
 * Review r24 segurança (B1): anonimizar (irreversível) leva o e-mail digitado (a intenção) e a prova recente (a senha atual
 * ou o código novo do e-mail). Sem um dos dois, nada vai (o botão fica desligado).
 */
export function corpoDaAnonimizacao(confirmacao: string, atual: string, codigo: string): { confirmacao: string; atual?: string; codigo?: string } | null {
  const prova = provaDaConfirmacao(atual, codigo);
  if (!confirmacao.includes('@') || (!prova.atual && !prova.codigo)) return null;
  return { confirmacao, ...prova };
}

/** Campos da confirmação (senha atual, código do e-mail e o botão que manda o código). */
function Confirmacao({ id, atual, codigo, aoMudarAtual, aoMudarCodigo }: { id: string; atual: string; codigo: string; aoMudarAtual: (v: string) => void; aoMudarCodigo: (v: string) => void }) {
  const { t } = useTranslation('portal');
  const toast = useToast();
  const [enviando, setEnviando] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-tinta-2">{t('perfil.confirmacaoTexto')}</p>
      <Rotulo id={`${id}-atual`} rotulo={t('perfil.senhaAtual')}>
        <Entrada id={`${id}-atual`} type="password" autoComplete="current-password" value={atual} onChange={(e) => aoMudarAtual(e.target.value)} />
      </Rotulo>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <Rotulo id={`${id}-codigo`} rotulo={t('perfil.codigoEmail')}>
            <Entrada id={`${id}-codigo`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={codigo} onChange={(e) => aoMudarCodigo(e.target.value.replace(/\D/g, ''))} />
          </Rotulo>
        </div>
        <Botao
          carregando={enviando}
          onClick={async () => {
            setEnviando(true);
            try {
              await ppost('/conta/codigo');
              toast.sucesso(t('perfil.codigoEnviado'));
            } catch (e) {
              toast.erro(mensagemDeErro(e));
            } finally {
              setEnviando(false);
            }
          }}
        >
          {t('perfil.enviarCodigo')}
        </Botao>
      </div>
    </div>
  );
}

function Passkeys() {
  const { t } = useTranslation('portal');
  const tn = useTranslation().t;
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  // Review r24 segurança (B1): a passkey a remover espera a mesma confirmação do cadastro.
  const [remover, setRemover] = useState<{ id: string; nome: string } | null>(null);
  const [atual, setAtual] = useState('');
  const [codigo, setCodigo] = useState('');
  const prova = provaDaConfirmacao(atual, codigo);
  const fechar = () => {
    setConfirmar(false);
    setRemover(null);
    setAtual('');
    setCodigo('');
  };
  const posicaoPasskey = useRef(-1);
  const q = useQuery({ queryKey: ['portal', 'passkeys'], queryFn: () => pget<{ itens: { id: string; nome: string; ultimo_uso_em: string | null }[] }>('/conta/passkeys') });
  if (!temPasskey()) return null;
  return (
    <Cartao titulo={t('perfil.passkeys')}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-2">{t('perfil.passkeysTexto')}</p>
        {q.data?.itens.length ? (
          <ul className="divide-y divide-borda" data-passkeys>
            {q.data.itens.map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{p.nome}</span>
                  {p.ultimo_uso_em && <span className="block text-xs text-tinta-3">{t('perfil.passkeyUsada', { data: dataCurta(p.ultimo_uso_em, document.documentElement.lang.slice(0, 2)) })}</span>}
                </span>
                <Botao
                  tamanho="sm"
                  variante="perigo"
                  data-remover-passkey=""
                  onClick={(e) => {
                    posicaoPasskey.current = posicaoNaPagina('[data-remover-passkey]', e.currentTarget);
                    setRemover({ id: p.id, nome: p.nome });
                  }}
                >
                  {t('perfil.removerPasskey')}
                </Botao>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-tinta-3">{t('perfil.semPasskey')}</p>
        )}
        <Botao className="w-fit" icone={<Fingerprint className="size-4" aria-hidden />} onClick={() => setConfirmar(true)} data-cadastrar-passkey="">
          {t('perfil.cadastrarPasskey')}
        </Botao>
      </div>
      <Modal
        aberto={confirmar || !!remover}
        aoFechar={fechar}
        titulo={t('perfil.confirmarPasskey')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={fechar}>
              {tn('comum.cancelar')}
            </Botao>
            {remover ? (
              <Botao
                variante="perigo"
                disabled={!prova.atual && !prova.codigo}
                onClick={async () => {
                  try {
                    await removerPasskey(remover.id, prova);
                    fechar();
                    toast.sucesso(t('perfil.passkeyRemovida'));
                    await qc.invalidateQueries({ queryKey: ['portal', 'passkeys'] });
                    // Review r29 visual (B2): a linha some; o foco vai ao "Remover" que ficou no lugar ou ao "Cadastrar passkey".
                    recuperarFoco({ seletor: '[data-remover-passkey]', i: posicaoPasskey.current, reservas: ['[data-cadastrar-passkey]'] });
                  } catch (e) {
                    toast.erro(mensagemDeErro(e));
                  }
                }}
              >
                {t('perfil.removerPasskey')}
              </Botao>
            ) : (
              <Botao
                variante="primario"
                icone={<Fingerprint className="size-4" aria-hidden />}
                disabled={!prova.atual && !prova.codigo}
                onClick={async () => {
                  try {
                    await cadastrarPasskey(nomeDoAparelho(), prova);
                    fechar();
                    toast.sucesso(t('perfil.passkeyCadastrada'));
                    await qc.invalidateQueries({ queryKey: ['portal', 'passkeys'] });
                  } catch (e) {
                    if (e instanceof DOMException || (e instanceof Error && e.message === 'cancelado')) return;
                    toast.erro(mensagemDeErro(e));
                  }
                }}
              >
                {t('perfil.cadastrarPasskey')}
              </Botao>
            )}
          </>
        }
      >
        {remover && <p className="mb-3 text-sm text-tinta-2">{t('perfil.removerPasskeyTexto', { nome: remover.nome })}</p>}
        <Confirmacao id="passkey-confirmacao" atual={atual} codigo={codigo} aoMudarAtual={setAtual} aoMudarCodigo={setCodigo} />
      </Modal>
    </Cartao>
  );
}

/** Perfil: idioma, tema, tamanho do texto, avisos, senha opcional, sair dos outros aparelhos, termo e LGPD. */
export function Perfil({ aoSair }: { aoSair: () => void }) {
  const { t, i18n } = useTranslation('portal');
  const tn = useTranslation().t;
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const idioma = i18n.language.slice(0, 2) as Idioma;
  const [nome, setNome] = useState(s.conta?.nome ?? '');
  const [atual, setAtual] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nova, setNova] = useState('');
  const prova = provaDaConfirmacao(atual, codigo);
  const [tema, setTema] = useState<'sistema' | 'claro' | 'escuro'>(() => (lido('malgo.portal.tema') as 'claro' | 'escuro' | null) ?? 'sistema');
  const [escala, setEscala] = useState<EscalaPortal>(() => (lido('malgo.portal.escala') as '110' | '125' | '150' | null) ?? '100');
  const simples = useModoSimples();
  const [anonimizar, setAnonimizar] = useState(false);
  const [confirmacao, setConfirmacao] = useState('');
  const [anonAtual, setAnonAtual] = useState('');
  const [anonCodigo, setAnonCodigo] = useState('');
  const corpoAnonimizar = corpoDaAnonimizacao(confirmacao, anonAtual, anonCodigo);
  const [erro, setErro] = useState<string | null>(null);
  const termo = useQuery({
    queryKey: ['portal', 'termo'],
    queryFn: () => pget<{ termo: { versao: string; aceito_em: string | null } | null }>('/termo'),
    enabled: !s.somente_leitura,
  });
  const leitura = s.somente_leitura;
  const executar = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) toast.sucesso(ok);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight">{t('perfil.titulo')}</h1>
      <div className="grid gap-4 md:grid-cols-2">
        <Cartao>
          <dl className="grid gap-3 text-sm">
            <div>
              <dt className="kicker">{t('perfil.empresa')}</dt>
              <dd>
                {nomeEmpresa(s)} · {s.empresa.escritorio}
              </dd>
            </div>
            <div>
              <dt className="kicker">{t('perfil.papel')}</dt>
              <dd>{t(`papel.${s.papel}`)}</dd>
            </div>
            {s.conta && (
              <div>
                <dt className="kicker">{t('entrar.email')}</dt>
                <dd className="break-all">{s.conta.email}</dd>
              </div>
            )}
            {termo.data?.termo?.aceito_em && (
              <div>
                <dd className="text-xs text-tinta-3">{t('perfil.termo', { versao: termo.data.termo.versao, data: dataCurta(termo.data.termo.aceito_em, idioma) })}</dd>
              </div>
            )}
          </dl>
          {!leitura && (
            <form
              className="mt-4 flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void executar(async () => {
                  await ppatch('/conta', { nome: nome.trim() });
                  await qc.invalidateQueries({ queryKey: CHAVE_SESSAO });
                }, tn('comum.salvo'));
              }}
            >
              <div className="flex-1">
                <Rotulo id="perfil-nome" rotulo={t('perfil.nome')}>
                  <Entrada id="perfil-nome" value={nome} maxLength={120} onChange={(e) => setNome(e.target.value)} />
                </Rotulo>
              </div>
              <Botao type="submit" disabled={!nome.trim()}>
                {t('perfil.salvarNome')}
              </Botao>
            </form>
          )}
        </Cartao>
        <Cartao>
          <div className="flex flex-col gap-4">
            <Rotulo id="perfil-idioma" rotulo={t('perfil.idioma')}>
              <Selecao
                id="perfil-idioma"
                value={idioma}
                onChange={(e) => {
                  const novo = e.target.value as Idioma;
                  void trocarIdioma(novo);
                  if (!leitura) void ppatch('/conta', { idioma: novo }).catch(() => undefined);
                }}
                opcoes={[
                  { valor: 'pt', rotulo: 'Português' },
                  { valor: 'en', rotulo: 'English' },
                  { valor: 'es', rotulo: 'Español' },
                ]}
              />
            </Rotulo>
            <Rotulo id="perfil-tema" rotulo={t('perfil.tema')}>
              <Selecao
                id="perfil-tema"
                value={tema}
                onChange={(e) => {
                  const v = e.target.value as 'sistema' | 'claro' | 'escuro';
                  setTema(v);
                  aplicarTema(v);
                }}
                opcoes={[
                  { valor: 'sistema', rotulo: t('perfil.temaSistema') },
                  { valor: 'claro', rotulo: t('perfil.temaClaro') },
                  { valor: 'escuro', rotulo: t('perfil.temaEscuro') },
                ]}
              />
            </Rotulo>
            <Rotulo id="perfil-fonte" rotulo={t('perfil.fonte')}>
              <Selecao
                id="perfil-fonte"
                value={escala}
                onChange={(e) => {
                  const v = e.target.value as EscalaPortal;
                  setEscala(v);
                  aplicarEscala(v);
                }}
                opcoes={[
                  { valor: '100', rotulo: t('perfil.fonteNormal') },
                  { valor: '110', rotulo: t('perfil.fonteMaior') },
                  { valor: '125', rotulo: t('perfil.fonteGrande') },
                  { valor: '150', rotulo: t('perfil.fonteMuitoGrande') },
                ]}
              />
            </Rotulo>
            {/* U15 F5: modo simples (desligado por padrão): o menu fica só com o essencial; nada perde o acesso por link. */}
            <Rotulo id="perfil-simples" rotulo={t('perfil.simples')} ajuda={t('perfil.simplesAjuda')}>
              <Selecao
                id="perfil-simples"
                value={simples ? 'ligado' : 'desligado'}
                onChange={(e) => definirModoSimples(e.target.value === 'ligado')}
                opcoes={[
                  { valor: 'desligado', rotulo: t('perfil.simplesDesligado') },
                  { valor: 'ligado', rotulo: t('perfil.simplesLigado') },
                ]}
              />
            </Rotulo>
          </div>
        </Cartao>
        <Avisos />
        {!leitura && <Passkeys />}
        {!leitura && (
          <Cartao titulo={t('perfil.seguranca')}>
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void executar(async () => {
                  await ppost('/conta/senha', { ...prova, nova });
                  setAtual('');
                  setCodigo('');
                  setNova('');
                }, t('perfil.senhaSalva'));
              }}
            >
              <p className="text-sm text-tinta-2">{t('perfil.senhaTexto')}</p>
              <Confirmacao id="perfil-senha" atual={atual} codigo={codigo} aoMudarAtual={setAtual} aoMudarCodigo={setCodigo} />
              <Rotulo id="perfil-senha-nova" rotulo={t('perfil.senhaNova')}>
                <Entrada id="perfil-senha-nova" type="password" autoComplete="new-password" minLength={10} value={nova} onChange={(e) => setNova(e.target.value)} />
              </Rotulo>
              <div className="flex flex-wrap gap-2">
                <Botao type="submit" variante="primario" disabled={nova.length < 10 || (!prova.atual && !prova.codigo)}>
                  {t('perfil.salvarSenha')}
                </Botao>
                <Botao disabled={!prova.atual && !prova.codigo} onClick={() => void executar(() => ppost('/conta/senha', { ...prova, nova: null }), t('perfil.senhaSalva'))}>
                  {t('perfil.removerSenha')}
                </Botao>
              </div>
              <Botao
                variante="fantasma"
                icone={<LogOut className="size-4" aria-hidden />}
                onClick={() =>
                  void executar(async () => {
                    const r = await sairDeTodos({ push, enviar: (corpo) => ppost<{ encerradas: number }>('/sessao/sair-de-todos', corpo) });
                    toast.sucesso(t('perfil.sairTodosFeito', { count: r.encerradas }));
                  })
                }
              >
                {t('perfil.sairTodos')}
              </Botao>
            </form>
          </Cartao>
        )}
        {!leitura && (
          <Cartao titulo={t('perfil.dados')}>
            <div className="flex flex-col gap-3">
              <p className="text-sm text-tinta-2">{t('perfil.dadosTexto')}</p>
              <div className="flex flex-wrap gap-2">
                <Botao icone={<Download className="size-4" aria-hidden />} onClick={() => void executar(() => baixar('/conta/exportar', 'meus-dados-portal.json'))}>
                  {t('perfil.exportar')}
                </Botao>
                <Botao variante="perigo" onClick={() => setAnonimizar(true)}>
                  {t('perfil.anonimizar')}
                </Botao>
              </div>
            </div>
          </Cartao>
        )}
      </div>
      <Botao className="w-fit md:hidden" variante="fantasma" icone={<LogOut className="size-4" aria-hidden />} onClick={aoSair}>
        {t('menu.sair')}
      </Botao>
      <Modal
        aberto={anonimizar}
        aoFechar={() => setAnonimizar(false)}
        titulo={t('perfil.anonimizarTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAnonimizar(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao
              variante="perigo"
              disabled={!corpoAnonimizar}
              onClick={async () => {
                if (!corpoAnonimizar) return;
                setErro(null);
                try {
                  await chamar('POST', '/conta/anonimizar', corpoAnonimizar);
                  aoSair();
                } catch (e) {
                  setErro(mensagemDeErro(e));
                }
              }}
            >
              {t('perfil.anonimizar')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 text-sm text-tinta-2">{t('perfil.anonimizarTexto')}</p>
        {erro && (
          <p role="alert" className="mb-3 text-sm text-perigo">
            {erro}
          </p>
        )}
        <Rotulo id="anonimizar-email" rotulo={t('perfil.confirmacao')}>
          <Entrada id="anonimizar-email" type="email" value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} />
        </Rotulo>
        <div className="mt-4">
          <Confirmacao id="anonimizar-confirmacao" atual={anonAtual} codigo={anonCodigo} aoMudarAtual={setAnonAtual} aoMudarCodigo={setAnonCodigo} />
        </div>
      </Modal>
    </div>
  );
}
